/**
 * /accept-invite — Phase 3.6.C magic-link first-sign-in surface.
 *
 * Lands here from the email link with `?token=<256-bit-token>`. The
 * token is the access capability; pre-auth route, no Cognito session.
 *
 * Flow:
 *   1. On mount → POST /api/invitations/validate (skipAuth)
 *      • valid → render "Set your password" form pre-filled with email + name
 *      • expired/used/invalid → error banner + "Request a new invite" copy
 *   2. Submit → POST /api/invitations/accept (skipAuth) — BE calls
 *      AdminSetUserPassword(Permanent: true) + marks the invitation consumed.
 *   3. Sign in via the existing Cognito SDK with (email, chosenPassword),
 *      then navigate to `/`.
 *
 * If the post-accept signIn fails (transient network blip, etc.) we
 * route the user to /login with a "Your password is set, please sign
 * in" banner — they're whole, just need one extra click. Don't try to
 * retry signIn silently here; fail open.
 */

import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import type { TFunction } from 'i18next'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { signIn } from '@/auth/cognito'
import { Button } from '@/components/Button'
import { FormField } from '@/components/FormField'
import { TermsNotice } from '@/features/auth/TermsNotice'
import { useAcceptInvite, useValidateInvite } from '@/features/auth/use-invitations'

interface AcceptInviteSearch {
  token?: string
}

export const Route = createFileRoute('/accept-invite')({
  component: AcceptInvitePage,
  validateSearch: (search: Record<string, unknown>): AcceptInviteSearch => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
})

const PASSWORD_MIN = 12

function describeValidationError(err: unknown, t: TFunction<'auth'>): string {
  if (err instanceof ApiError && typeof err.message === 'string' && err.message) return err.message
  if (err instanceof Error && err.message) return err.message
  return t('acceptInvite.errorInvalidLinkFallback')
}

function describeAcceptError(err: unknown, t: TFunction<'auth'>): string {
  if (err instanceof ApiError) {
    if (err.status === 400 && err.message) return err.message
    return err.message || t('acceptInvite.errorAcceptFallback')
  }
  if (err instanceof Error && err.message) return err.message
  return t('acceptInvite.errorAcceptFallback')
}

function AcceptInvitePage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const { token } = Route.useSearch()
  const validation = useValidateInvite(token ?? null)
  const accept = useAcceptInvite()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // No token at all → render an error directly. Don't kick off the
  // mutation with an empty string (the BE would 400 anyway, but this
  // skips the round-trip).
  if (!token) {
    return (
      <Shell>
        <ErrorPanel
          title={t('acceptInvite.errorIncompleteLinkTitle')}
          message={t('acceptInvite.errorIncompleteLinkMessage')}
          alreadyHaveAccount={t('acceptInvite.alreadyHaveAccount')}
          signInLink={t('acceptInvite.signInLink')}
        />
      </Shell>
    )
  }

  if (validation.isPending) {
    return (
      <Shell>
        <div className="rounded-lg border border-border bg-bg-elevated p-6 shadow-sm">
          <p className="text-sm text-fg-muted">{t('acceptInvite.verifying')}</p>
        </div>
      </Shell>
    )
  }

  if (validation.isError || !validation.data) {
    return (
      <Shell>
        <ErrorPanel
          title={t('acceptInvite.errorInvalidLinkTitle')}
          message={describeValidationError(validation.error, t)}
          alreadyHaveAccount={t('acceptInvite.alreadyHaveAccount')}
          signInLink={t('acceptInvite.signInLink')}
        />
      </Shell>
    )
  }

  const { email, name } = validation.data

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)

    if (password.length < PASSWORD_MIN) {
      setError(t('acceptInvite.errorPasswordTooShort', { minLength: PASSWORD_MIN }))
      return
    }
    if (password !== confirm) {
      setError(t('acceptInvite.errorPasswordsMismatch'))
      return
    }

    setSubmitting(true)
    try {
      await accept.mutateAsync({ token, password })
    } catch (err) {
      setError(describeAcceptError(err, t))
      setSubmitting(false)
      return
    }

    // Password is set. Sign the user in via the regular SDK flow so
    // they land logged in on `/`. If signIn fails (transient), bounce
    // them to /login — they have a working password now.
    try {
      await signIn(email, password)
      navigate({ to: '/' })
    } catch {
      navigate({ to: '/login' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Shell>
      <div className="rounded-lg border border-border bg-bg-elevated p-6 shadow-sm">
        <h1 className="mb-1 text-2xl font-semibold text-fg">{t('acceptInvite.title')}</h1>
        <p className="mb-6 text-sm text-fg-muted">
          {t('acceptInvite.subtitle', { firstName: name.split(' ')[0] || name })}
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <FormField
            id="invite-email"
            label={t('acceptInvite.emailLabel')}
            type="email"
            value={email}
            disabled
            readOnly
          />
          <FormField
            id="invite-password"
            label={t('acceptInvite.passwordLabel')}
            type="password"
            autoComplete="new-password"
            required
            minLength={PASSWORD_MIN}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              if (error) setError(null)
            }}
          />
          <FormField
            id="invite-password-confirm"
            label={t('acceptInvite.confirmPasswordLabel')}
            type="password"
            autoComplete="new-password"
            required
            minLength={PASSWORD_MIN}
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value)
              if (error) setError(null)
            }}
          />

          <p className="text-xs text-fg-muted">
            {t('acceptInvite.passwordRequirements', { minLength: PASSWORD_MIN })}
          </p>

          {error && (
            <p
              role="alert"
              className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/40 dark:text-red-200"
            >
              {error}
            </p>
          )}

          <Button type="submit" disabled={submitting} className="w-full">
            {submitting ? t('acceptInvite.submitting') : t('acceptInvite.submit')}
          </Button>
        </form>
        <TermsNotice className="mt-4" />
      </div>
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm">{children}</div>
    </div>
  )
}

function ErrorPanel({
  title,
  message,
  alreadyHaveAccount,
  signInLink,
}: {
  title: string
  message: string
  alreadyHaveAccount: string
  signInLink: string
}) {
  return (
    <div className="rounded-lg border border-border bg-bg-elevated p-6 shadow-sm">
      <h1 className="mb-1 text-xl font-semibold text-fg">{title}</h1>
      <p className="mb-6 text-sm text-fg-muted">{message}</p>
      <p className="text-sm text-fg-muted">
        {alreadyHaveAccount}{' '}
        <Link
          to="/login"
          className="text-fg underline decoration-fg-subtle underline-offset-2 hover:decoration-fg"
        >
          {signInLink}
        </Link>
      </p>
    </div>
  )
}
